'use client';

import React, { useEffect, useState, useRef } from 'react';
import { distractionImageService, DistractionImage } from '@/lib/services/distractionImageService';

interface FallingItem {
  id: string;
  imageUrl: string;
  left: number;       // % from left
  size: number;       // px
  duration: number;   // animation duration seconds
  delay: number;      // animation delay seconds
  rotation: number;   // degrees
  swayAmount: number; // px horizontal sway
}

function generateItems(images: DistractionImage[], count: number): FallingItem[] {
  if (images.length === 0) return [];
  return Array.from({ length: count }, (_, i) => {
    const img = images[i % images.length];
    return {
      id: `fall-${i}-${Date.now()}`,
      imageUrl: img.imageUrl,
      left: Math.random() * 90 + 5,
      size: Math.floor(Math.random() * 30) + 32, // 32–62px
      duration: Math.random() * 4 + 4,            // 4–8s
      delay: Math.random() * 6,                   // 0–6s
      rotation: Math.random() * 360,
      swayAmount: Math.floor(Math.random() * 40) - 20,
    };
  });
}

interface FallingManateesProps {
  active: boolean;
}

export default function FallingManatees({ active }: FallingManateesProps) {
  const [images, setImages] = useState<DistractionImage[]>([]);
  const [items, setItems] = useState<FallingItem[]>([]);
  const intervalRef = useRef<ReturnType<typeof setInterval> | null>(null);

  useEffect(() => {
    distractionImageService.getAll().then((imgs) => {
      setImages(imgs);
    });
  }, []);

  useEffect(() => {
    if (!active || images.length === 0) {
      setItems([]);
      if (intervalRef.current) clearInterval(intervalRef.current);
      return;
    }

    // Initial batch
    setItems(generateItems(images, 8));

    // Refresh items every 8s to keep them falling
    intervalRef.current = setInterval(() => {
      setItems(generateItems(images, 8));
    }, 8000);

    return () => {
      if (intervalRef.current) clearInterval(intervalRef.current);
    };
  }, [active, images]);

  if (!active || items.length === 0) return null;

  return (
    <div className="fixed inset-0 pointer-events-none z-20 overflow-hidden">
      {items.map((item) => (
        <div
          key={item.id}
          className="absolute top-0"
          style={{
            left: `${item.left}%`,
            animationName: 'manatee-fall',
            animationDuration: `${item.duration}s`,
            animationDelay: `${item.delay}s`,
            animationTimingFunction: 'linear',
            animationFillMode: 'both',
            animationIterationCount: 'infinite',
          }}
        >
          {/* eslint-disable-next-line @next/next/no-img-element */}
          <img
            src={item.imageUrl}
            alt="falling manatee"
            style={{
              width: `${item.size}px`,
              height: `${item.size}px`,
              objectFit: 'contain',
              transform: `rotate(${item.rotation}deg)`,
              opacity: 0.85,
            }}
          />
        </div>
      ))}
    </div>
  );
}
